import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Bold,
  Italic,
  Heading2,
  Heading3,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Code as CodeIcon,
  Image as ImageIcon,
  Save,
  ArrowLeft,
  Upload,
  Trash2,
  ExternalLink,
} from "lucide-react";
import AdminLayout from "./AdminLayout";
import {
  adminCreatePost,
  adminGetPost,
  adminUpdatePost,
} from "../../lib/api";
import { CATEGORIES } from "../../lib/categories";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

const initialState = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  cover_image: null,
  category: "company-setup",
  status: "draft",
  meta_title: "",
  meta_description: "",
};

const MAX_IMG_BYTES = 2 * 1024 * 1024; // 2 MB

function autoSlug(title) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export default function AdminPostEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!isNew) {
      adminGetPost(id)
        .then((p) => {
          setForm({
            title: p.title || "",
            slug: p.slug || "",
            excerpt: p.excerpt || "",
            content: p.content || "",
            cover_image: p.cover_image || null,
            category: p.category || "company-setup",
            status: p.status || "draft",
            meta_title: p.meta_title || "",
            meta_description: p.meta_description || "",
          });
          setSlugTouched(true);
        })
        .catch((err) => {
          if (err?.response?.status === 401) {
            navigate("/admin/login", { replace: true });
            return;
          }
          toast.error("Could not load post.");
          navigate("/admin", { replace: true });
        })
        .finally(() => setLoading(false));
    }
  }, [id, isNew, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onTitleChange = (v) => {
    update("title", v);
    if (!slugTouched) update("slug", autoSlug(v));
  };

  const onCoverUpload = async (file) => {
    if (!file) return;
    if (file.size > MAX_IMG_BYTES) {
      toast.error("Cover image must be 2 MB or smaller.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => update("cover_image", reader.result);
    reader.onerror = () => toast.error("Could not read image.");
    reader.readAsDataURL(file);
  };

  const insertAtCursor = (before, after = "") => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = form.content.slice(start, end);
    const next =
      form.content.slice(0, start) + before + sel + after + form.content.slice(end);
    update("content", next);
    requestAnimationFrame(() => {
      ta.focus();
      const cursorAt = sel ? start + before.length + sel.length + after.length : start + before.length;
      ta.setSelectionRange(cursorAt, cursorAt);
    });
  };

  const TOOLS = [
    { icon: Bold, title: "Bold", run: () => insertAtCursor("**", "**") },
    { icon: Italic, title: "Italic", run: () => insertAtCursor("*", "*") },
    { icon: Heading2, title: "Heading 2", run: () => insertAtCursor("\n## ") },
    { icon: Heading3, title: "Heading 3", run: () => insertAtCursor("\n### ") },
    { icon: List, title: "Bullet list", run: () => insertAtCursor("\n- ") },
    { icon: ListOrdered, title: "Numbered list", run: () => insertAtCursor("\n1. ") },
    { icon: Quote, title: "Quote", run: () => insertAtCursor("\n> ") },
    { icon: CodeIcon, title: "Inline code", run: () => insertAtCursor("`", "`") },
    {
      icon: LinkIcon,
      title: "Link",
      run: () => {
        const url = window.prompt("Link URL (https://…)");
        if (!url) return;
        insertAtCursor("[", `](${url})`);
      },
    },
    {
      icon: ImageIcon,
      title: "Inline image",
      run: () => {
        const url = window.prompt("Image URL (https://…)");
        if (!url) return;
        insertAtCursor(`![alt text](${url})\n`);
      },
    },
  ];

  const onSave = async (publish) => {
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    if (form.title.trim().length < 2) {
      toast.error("Title must be at least 2 characters.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      title: form.title.trim(),
      slug: (form.slug || autoSlug(form.title)).trim(),
      status: publish ? "published" : form.status,
    };
    try {
      let saved;
      if (isNew) {
        saved = await adminCreatePost(payload);
        toast.success(publish ? "Post published." : "Draft saved.");
      } else {
        saved = await adminUpdatePost(id, payload);
        toast.success(publish ? "Post published." : "Changes saved.");
      }
      if (isNew) {
        navigate(`/admin/posts/${saved.id}/edit`, { replace: true });
      } else {
        setForm((f) => ({ ...f, status: saved.status, slug: saved.slug }));
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(", ")
        : detail || err?.message || "Could not save post.";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const isPublished = form.status === "published";
  const wordCount = useMemo(
    () => (form.content.trim() ? form.content.trim().split(/\s+/).length : 0),
    [form.content]
  );

  if (loading) {
    return (
      <AdminLayout>
        <p className="text-sm text-slate-500">Loading…</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <button
        onClick={() => navigate("/admin")}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#3366FF] mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back to posts
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main editor column */}
        <div className="lg:col-span-2 space-y-6">
          <div>
            <input
              data-testid="post-title"
              type="text"
              value={form.title}
              onChange={(e) => onTitleChange(e.target.value)}
              placeholder="Post title…"
              className="w-full font-heading font-light text-3xl md:text-4xl tracking-tight text-[#0F172A] bg-transparent border-0 border-b-2 border-slate-200 pb-3 focus:outline-none focus:border-[#3366FF]"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">
              Excerpt
            </label>
            <textarea
              data-testid="post-excerpt"
              rows={2}
              value={form.excerpt}
              onChange={(e) => update("excerpt", e.target.value)}
              maxLength={400}
              placeholder="One-line summary that shows on the blog grid."
              className="mt-2 w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-[#3366FF]"
            />
            <p className="mt-1 text-xs text-slate-400">{form.excerpt.length}/400</p>
          </div>

          {/* Markdown toolbar */}
          <div className="border border-slate-200 rounded-sm bg-white">
            <div className="flex flex-wrap items-center gap-1 px-2 py-2 border-b border-slate-200 bg-slate-50">
              {TOOLS.map(({ icon: Icon, title, run }) => (
                <button
                  key={title}
                  type="button"
                  onClick={run}
                  title={title}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-slate-600 hover:bg-white hover:text-[#0F172A] hover:shadow-sm transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
              <span className="ml-auto text-xs text-slate-400 px-2">
                {wordCount} words · Markdown
              </span>
            </div>
            <textarea
              ref={textareaRef}
              data-testid="post-content"
              rows={18}
              value={form.content}
              onChange={(e) => update("content", e.target.value)}
              placeholder="Start writing…  Use the toolbar above or type Markdown directly. **bold**, *italic*, ## Heading."
              className="w-full px-4 py-4 font-mono text-[14px] leading-relaxed text-[#0F172A] bg-white focus:outline-none rounded-b-sm"
            />
          </div>

          {/* Live preview */}
          <details className="border border-slate-200 rounded-sm bg-white" open>
            <summary className="cursor-pointer select-none px-4 py-3 font-heading text-sm font-semibold text-[#0F172A] bg-slate-50 border-b border-slate-200">
              Live preview
            </summary>
            <article className="prose prose-slate max-w-none p-6">
              {form.content.trim() ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {form.content}
                </ReactMarkdown>
              ) : (
                <p className="text-slate-400 italic">Nothing to preview yet.</p>
              )}
            </article>
          </details>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="border border-slate-200 rounded-sm bg-white p-5 space-y-4">
            <h3 className="font-heading font-semibold text-sm text-[#0F172A]">
              Publish
            </h3>
            <div className="text-xs text-slate-500">
              Status:{" "}
              <span
                className={
                  isPublished
                    ? "text-emerald-600 font-semibold"
                    : "text-slate-700 font-semibold"
                }
              >
                {isPublished ? "Published" : "Draft"}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <button
                data-testid="post-save-draft"
                disabled={saving}
                onClick={() => onSave(false)}
                className="btn-ghost-navy w-full justify-center disabled:opacity-60"
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving…" : isPublished ? "Save changes" : "Save draft"}
              </button>
              <button
                data-testid="post-publish"
                disabled={saving}
                onClick={() => onSave(true)}
                className="btn-gold w-full justify-center disabled:opacity-60"
              >
                {isPublished ? "Update published" : "Publish now"}
              </button>
              {isPublished && form.slug && (
                <a
                  href={`/blog/${form.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[#3366FF] hover:underline inline-flex items-center gap-1 mt-1"
                >
                  <ExternalLink className="w-3 h-3" /> View live
                </a>
              )}
            </div>
          </div>

          <div className="border border-slate-200 rounded-sm bg-white p-5 space-y-4">
            <h3 className="font-heading font-semibold text-sm text-[#0F172A]">
              Cover image
            </h3>
            {form.cover_image ? (
              <div className="space-y-3">
                <img
                  src={form.cover_image}
                  alt="Cover"
                  className="w-full aspect-[16/10] object-cover rounded-sm"
                />
                <button
                  type="button"
                  onClick={() => update("cover_image", null)}
                  className="text-xs text-red-600 hover:underline inline-flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Remove
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-sm py-8 cursor-pointer hover:border-[#3366FF] hover:bg-slate-50 transition-colors">
                <Upload className="w-5 h-5 text-slate-400" />
                <span className="text-xs text-slate-500">
                  Click to upload (max 2 MB)
                </span>
                <input
                  data-testid="post-cover-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onCoverUpload(e.target.files?.[0])}
                />
              </label>
            )}
          </div>

          <div className="border border-slate-200 rounded-sm bg-white p-5 space-y-4">
            <h3 className="font-heading font-semibold text-sm text-[#0F172A]">
              Category
            </h3>
            <Select value={form.category} onValueChange={(v) => update("category", v)}>
              <SelectTrigger
                data-testid="post-category"
                className="w-full bg-white border border-slate-300 rounded-sm h-10 text-sm"
              >
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-slate-200">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border border-slate-200 rounded-sm bg-white p-5 space-y-4">
            <h3 className="font-heading font-semibold text-sm text-[#0F172A]">
              SEO
            </h3>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">
                URL slug
              </label>
              <div className="mt-2 flex items-center border border-slate-300 rounded-sm bg-white">
                <span className="text-xs text-slate-400 pl-3 font-mono">/blog/</span>
                <input
                  data-testid="post-slug"
                  type="text"
                  value={form.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    update("slug", autoSlug(e.target.value));
                  }}
                  className="flex-1 px-2 py-2 text-sm font-mono text-[#0F172A] focus:outline-none rounded-r-sm"
                  placeholder="post-url"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">
                Meta title
              </label>
              <input
                data-testid="post-meta-title"
                type="text"
                value={form.meta_title}
                onChange={(e) => update("meta_title", e.target.value)}
                maxLength={200}
                className="mt-2 w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3366FF]"
                placeholder={form.title || "Defaults to post title"}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">
                Meta description
              </label>
              <textarea
                data-testid="post-meta-desc"
                rows={3}
                value={form.meta_description}
                onChange={(e) => update("meta_description", e.target.value)}
                maxLength={400}
                className="mt-2 w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3366FF]"
                placeholder={form.excerpt || "Defaults to excerpt"}
              />
              <p className="mt-1 text-xs text-slate-400">
                {form.meta_description.length}/400 — aim for 140-160.
              </p>
            </div>
          </div>
        </aside>
      </div>
    </AdminLayout>
  );
}
